/**
 * Client for the Te Pone admin API.
 *
 * In deployed environments requests go through the same-origin Vercel proxy
 * (`/api/tepone`, see src/server/teponeProxy.ts) which injects the shared bot
 * key server-side. For local `vite dev` (no /api functions) set
 * `VITE_TEPONE_API_URL` (+ `VITE_TEPONE_API_KEY`) to talk to a bot directly.
 */

export type TeponeAdminCode = {
  token: string;
  code: string;
  status:
    | "minted"
    | "opened"
    | "locked"
    | "ready"
    | "claimed"
    | "delivered"
    | "expired"
    | "void"
    | string;
  /** Internal dev code (dev-* batch): never expires, resets after delivery. */
  dev?: boolean;
  batch: string | null;
  event_label: string | null;
  narration_language: string | null;
  wa_id: string | null;
  workload_id: string | null;
  media_count: number;
  created_at: string | null;
  opened_at: string | null;
  ready_at: string | null;
  claimed_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  portal_url: string;
  wa_link: string;
};

function apiTarget(): { base: string; headers: Record<string, string> } {
  const direct = import.meta.env.VITE_TEPONE_API_URL as string | undefined;
  if (direct) {
    const key = import.meta.env.VITE_TEPONE_API_KEY as string | undefined;
    return {
      base: `${direct.replace(/\/$/, "")}/tepone/v1`,
      headers: key ? { "X-Tepone-Key": key } : {},
    };
  }
  const proxy =
    (import.meta.env.VITE_TEPONE_PROXY_PATH as string | undefined) ||
    "/api/tepone";
  return { base: proxy.replace(/\/$/, ""), headers: {} };
}

async function teponeRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { base, headers } = apiTarget();
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...headers,
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.error || `Te Pone API error ${response.status}`);
  }
  if (payload === null) {
    // An HTML body with a 200 means the SPA catch-all swallowed the proxy
    // route (vercel.json rewrite missing) — surface it instead of rendering
    // an empty list.
    throw new Error(
      "Te Pone API returned a non-JSON response (proxy misrouted)",
    );
  }
  return payload as T;
}

export async function listTeponeCodes(limit = 200): Promise<TeponeAdminCode[]> {
  const { codes } = await teponeRequest<{ codes: TeponeAdminCode[] }>(
    `/admin/codes?limit=${limit}`,
  );
  return codes ?? [];
}

export async function mintTeponeCodes(
  event?: string,
  options?: { dev?: boolean },
): Promise<TeponeAdminCode[]> {
  const body: Record<string, unknown> = {};
  if (event) body.event = event;
  // Internal dev link: never expires + resets to fresh after each delivery,
  // so the same link runs the whole real flow repeatedly.
  if (options?.dev) body.dev = true;
  const { codes } = await teponeRequest<{ codes: TeponeAdminCode[] }>(
    "/admin/codes",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return codes ?? [];
}
